/* src/components/LoginButton.js */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

function safeJsonParse(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function log(...args) {
  try {
    // eslint-disable-next-line no-console
    console.log('[OAuthDebug]', new Date().toISOString(), ...args);
  } catch {}
}

function warn(...args) {
  try {
    // eslint-disable-next-line no-console
    console.warn('[OAuthDebug]', new Date().toISOString(), ...args);
  } catch {}
}

function err(...args) {
  try {
    // eslint-disable-next-line no-console
    console.error('[OAuthDebug]', new Date().toISOString(), ...args);
  } catch {}
}

function computeAuthorityOrigin() {
  if (typeof window === 'undefined' || !window.location) return '';
  const origin = window.location.origin || '';
  try {
    const url = new URL(origin);
    const host = url.host || '';
    if (host.endsWith('voxemera.com')) {
      const paHost = host.replace(/voxemera\.com$/, 'point-atlas.io');
      return `${url.protocol}//${paHost}`;
    }
    return origin;
  } catch {
    return origin;
  }
}

function computeConsumerOrigin() {
  if (typeof window === 'undefined' || !window.location) return '';
  const origin = window.location.origin || '';
  try {
    const url = new URL(origin);
    const host = url.host || '';
    if (host.endsWith('point-atlas.io')) {
      const vxHost = host.replace(/point-atlas\.io$/, 'voxemera.com');
      return `${url.protocol}//${vxHost}`;
    }
    if (host.endsWith('voxemera.com')) return origin;
    return '';
  } catch {
    return '';
  }
}

function isConsumerHost() {
  if (typeof window === 'undefined' || !window.location) return false;
  const host = window.location.host || '';
  return host.endsWith('voxemera.com');
}

function isAuthorityHost() {
  if (typeof window === 'undefined' || !window.location) return false;
  const host = window.location.host || '';
  return host.endsWith('point-atlas.io');
}

function buildUrl(baseOrigin, path) {
  if (!baseOrigin) return '';
  let p = String(path || '');
  if (!p.startsWith('/')) p = `/${p}`;
  return `${baseOrigin}${p}`;
}

async function fetchJsonWithCreds(url, reason) {
  try {
    log(`Fetching (${reason}) url: ${url}`);
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });

    const status = res.status;
    let body = null;
    try {
      const text = await res.text();
      body = safeJsonParse(text) || text;
    } catch {
      body = null;
    }

    log(`Fetch (${reason}) status: ${status}`, body);

    if (!res.ok || status === 401 || status === 403) {
      return { ok: false, status, body };
    }
    if (typeof body === 'object' && body !== null) {
      return { ok: true, status, body };
    }
    return { ok: false, status, body };
  } catch (e) {
    err('fetchJsonWithCreds error:', e);
    return { ok: false, status: 0, body: null, error: e };
  }
}

function normalizeRedirectPath(p) {
  if (!p) return '/';
  let s = String(p).trim();
  if (!s) return '/';
  if (!s.startsWith('/')) s = '/' + s;
  return s;
}

function buildTransferBeginUrl(authorityOrigin, consumerOrigin, redirectPath) {
  const begin = new URL(buildUrl(authorityOrigin, '/auth/transfer/begin'));
  begin.searchParams.set('return_to', consumerOrigin || '');
  begin.searchParams.set('redirect_path', redirectPath || '/');
  return begin.toString();
}

function buildAuthorityGoogleUrl(authorityOrigin, consumerOrigin, redirectPath) {
  const u = new URL(buildUrl(authorityOrigin, '/auth/google'));
  if (consumerOrigin) u.searchParams.set('return_to', consumerOrigin);
  if (redirectPath) u.searchParams.set('redirect_path', redirectPath);
  return u.toString();
}

function isLikelyOauthSuccessMessage(evt) {
  try {
    const data = evt && evt.data ? evt.data : null;
    if (!data || typeof data !== 'object') return false;
    const t = String(data.type || '').trim().toLowerCase();
    return t === 'oauth-success' || t === 'oauth_success' || t === 'auth-success' || t === 'auth_success';
  } catch {
    return false;
  }
}

function getAllowedOrigins(currentOrigin) {
  return new Set([
    currentOrigin,
    'https://point-atlas.io',
    'https://www.point-atlas.io',
    'https://beta.point-atlas.io',
    'https://voxemera.com',
    'https://www.voxemera.com',
    'https://beta.voxemera.com',
  ]);
}

function isOauthPopupWindow() {
  try {
    if (typeof window === 'undefined') return false;
    if (!window.opener) return false;
    if (window.opener === window) return false;
    if (window.closed) return false;
    const name = String(window.name || '');
    return name === 'pa_google_oauth';
  } catch {
    return false;
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function broadcastAuthChanged(currentOrigin, payload) {
  const data = payload || { type: 'oauth-success' };

  try {
    if (typeof window !== 'undefined' && window.BroadcastChannel) {
      const ch = new BroadcastChannel('pa_auth');
      try {
        ch.postMessage({ ...data, ts: Date.now(), via: 'bc' });
      } catch {}
      try {
        ch.close();
      } catch {}
    }
  } catch {}

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const key = 'pa_auth_event';
      const event = JSON.stringify({
        ...data,
        ts: Date.now(),
        via: 'storage',
        origin: currentOrigin || '',
      });
      try {
        window.localStorage.setItem(key, event);
      } catch {}
    }
  } catch {}
}

function tryNotifyOpenerAndClose(currentOrigin, payload) {
  const allowed = getAllowedOrigins(currentOrigin);
  const data = payload || { type: 'oauth-success' };

  try {
    if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
      for (const origin of allowed) {
        try {
          window.opener.postMessage(data, origin);
        } catch {}
      }
    }
  } catch {}

  broadcastAuthChanged(currentOrigin, data);

  try {
    if (typeof window !== 'undefined') {
      window.close();
    }
  } catch {}
}

function LoginButton({ user, setUser, small, label }) {
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [hasPopupBlocked, setHasPopupBlocked] = useState(false);
  const [error, setError] = useState(null);

  const authorityOrigin = useMemo(() => computeAuthorityOrigin(), []);
  const consumerOrigin = useMemo(() => computeConsumerOrigin(), []);
  const consumer = useMemo(() => isConsumerHost(), []);
  const authorityHost = useMemo(() => isAuthorityHost(), []);

  const currentOrigin = typeof window !== 'undefined' && window.location ? window.location.origin : '';

  const popupRef = useRef(null);
  const pollTimerRef = useRef(null);
  const closedRef = useRef(false);
  const lastRefreshMsRef = useRef(0);

  useEffect(() => {
    setError(null);
  }, [user]);

  const refreshMe = useCallback(
    async (reason) => {
      if (!setUser) return false;

      const now = Date.now();
      if (now - lastRefreshMsRef.current < 200) return false;
      lastRefreshMsRef.current = now;

      const meUrl = buildUrl(currentOrigin, '/api/user/me');
      const r = await fetchJsonWithCreds(meUrl, reason || 'refresh-me');
      if (r.ok && r.body) {
        setUser(r.body);
        return true;
      }
      return false;
    },
    [setUser, currentOrigin]
  );

  const refreshMeWithRetries = useCallback(
    async (reason) => {
      const attempts = 20;
      const delays = [
        0, 60, 90, 120, 150, 180, 220, 260, 320, 380,
        450, 520, 600, 700, 820, 950, 1100, 1300, 1500, 1750,
      ];

      for (let i = 0; i < attempts; i++) {
        if (i > 0) await sleep(delays[Math.min(i, delays.length - 1)]);
        const ok = await refreshMe(`${reason || 'refresh-retry'} #${i + 1}`);
        if (ok) return true;
      }
      return false;
    },
    [refreshMe]
  );

  useEffect(() => {
    let cancelled = false;

    async function initialCheck() {
      const meUrl = buildUrl(currentOrigin, '/api/user/me');
      const result = await fetchJsonWithCreds(meUrl, 'initial-check current-origin');
      if (cancelled) return;
      if (result.ok && result.body) {
        if (setUser) setUser(result.body);
      }
    }

    initialCheck();

    return () => {
      cancelled = true;
    };
  }, [setUser, currentOrigin]);

  const handleOauthSuccessSignal = useCallback(
    async (source) => {
      try {
        log('OAuth success signal via', source);

        await refreshMeWithRetries(`oauth-success ${source}`);

        try {
          const p = popupRef.current;
          if (p && !p.closed) p.close();
        } catch {}

        if (pollTimerRef.current) {
          try {
            clearInterval(pollTimerRef.current);
          } catch {}
          pollTimerRef.current = null;
        }

        if (!closedRef.current) setIsLoggingIn(false);
      } catch (e) {
        warn('handleOauthSuccessSignal error:', e);
      }
    },
    [refreshMeWithRetries]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onMessage = async (evt) => {
      try {
        if (!isLikelyOauthSuccessMessage(evt)) return;

        const src = evt && evt.origin ? String(evt.origin) : '';
        const allowed = getAllowedOrigins(currentOrigin);
        if (src && !allowed.has(src)) return;

        await handleOauthSuccessSignal(`postMessage from ${src || 'unknown'}`);
      } catch (e) {
        warn('onMessage handler error:', e);
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      try {
        window.removeEventListener('message', onMessage);
      } catch {}
    };
  }, [currentOrigin, handleOauthSuccessSignal]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.BroadcastChannel) return;

    let ch = null;

    const onBC = async (evt) => {
      try {
        const data = evt && evt.data ? evt.data : null;
        if (!data || typeof data !== 'object') return;
        const t = String(data.type || '').trim().toLowerCase();
        if (t !== 'oauth-success' && t !== 'oauth_success' && t !== 'auth-success' && t !== 'auth_success') return;

        await handleOauthSuccessSignal('BroadcastChannel');
      } catch (e) {
        warn('BroadcastChannel handler error:', e);
      }
    };

    try {
      ch = new BroadcastChannel('pa_auth');
      ch.addEventListener('message', onBC);
    } catch (e) {
      warn('Failed to create BroadcastChannel:', e);
      ch = null;
    }

    return () => {
      try {
        if (ch) {
          ch.removeEventListener('message', onBC);
          ch.close();
        }
      } catch {}
    };
  }, [handleOauthSuccessSignal]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onStorage = async (evt) => {
      try {
        if (!evt) return;
        if (String(evt.key || '') !== 'pa_auth_event') return;
        const v = evt.newValue || '';
        if (!v) return;

        const data = safeJsonParse(v);
        if (!data || typeof data !== 'object') return;

        const t = String(data.type || '').trim().toLowerCase();
        if (t !== 'oauth-success' && t !== 'oauth_success' && t !== 'auth-success' && t !== 'auth_success') return;

        await handleOauthSuccessSignal('storage');
      } catch (e) {
        warn('storage handler error:', e);
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      try {
        window.removeEventListener('storage', onStorage);
      } catch {}
    };
  }, [handleOauthSuccessSignal]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let focusTimer = null;

    const doRefresh = async (why) => {
      try {
        await refreshMeWithRetries(why);
      } catch {}
    };

    const onFocus = () => {
      doRefresh('window focus');
    };

    const onVisibility = () => {
      try {
        if (document && document.visibilityState === 'visible') {
          doRefresh('visibility visible');
        }
      } catch {}
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    focusTimer = setInterval(() => {
      try {
        if (document && document.visibilityState === 'visible') {
          doRefresh('visible heartbeat');
        }
      } catch {}
    }, 8000);

    return () => {
      try {
        window.removeEventListener('focus', onFocus);
      } catch {}
      try {
        document.removeEventListener('visibilitychange', onVisibility);
      } catch {}
      if (focusTimer) {
        try {
          clearInterval(focusTimer);
        } catch {}
      }
    };
  }, [refreshMeWithRetries]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isOauthPopupWindow()) return;

    let cancelled = false;
    let tries = 0;
    const maxTries = 60;
    const delay = 400;

    const run = async () => {
      while (!cancelled && tries < maxTries) {
        tries += 1;

        const meUrl = buildUrl(currentOrigin, '/api/user/me');
        const r = await fetchJsonWithCreds(meUrl, `popup-self-complete #${tries}`);
        if (cancelled) return;

        if (r.ok && r.body) {
          log('Popup detected valid session; notifying opener and closing.');
          tryNotifyOpenerAndClose(currentOrigin, { type: 'oauth-success' });
          return;
        }

        await sleep(delay);
      }

      if (!cancelled) {
        warn('Popup self-complete timed out; leaving popup open.');
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [currentOrigin]);

  const stopLoginFlow = useCallback(() => {
    try {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    } catch {}
    pollTimerRef.current = null;

    try {
      const p = popupRef.current;
      if (p && !p.closed) p.close();
    } catch {}
    popupRef.current = null;

    if (!closedRef.current) setIsLoggingIn(false);
  }, []);

  useEffect(() => {
    return () => {
      closedRef.current = true;
      try {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      } catch {}
      pollTimerRef.current = null;
      try {
        const p = popupRef.current;
        if (p && !p.closed) p.close();
      } catch {}
      popupRef.current = null;
    };
  }, []);

  const handleLoginClick = async () => {
    setError(null);
    setHasPopupBlocked(false);

    const redirectPath = normalizeRedirectPath('/comms');

    const popup = window.open(
      'about:blank',
      'pa_google_oauth',
      'width=500,height=650,menubar=no,toolbar=no,status=no,scrollbars=yes'
    );

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      warn('Popup window blocked or failed to open.');
      setHasPopupBlocked(true);
      return;
    }

    popupRef.current = popup;
    setIsLoggingIn(true);

    const authorityMeUrl = buildUrl(authorityOrigin, '/api/user/me');
    const currentMeUrl = buildUrl(currentOrigin, '/api/user/me');

    const targetConsumerOrigin = consumer ? currentOrigin : consumerOrigin;

    const transferBeginUrl = buildTransferBeginUrl(authorityOrigin, targetConsumerOrigin, redirectPath);
    const consumerMeUrl = buildUrl(targetConsumerOrigin, '/api/user/me');

    const shouldTransferToConsumer = !!targetConsumerOrigin;

    const authorityGoogleUrl = buildAuthorityGoogleUrl(
      authorityOrigin,
      shouldTransferToConsumer ? targetConsumerOrigin : '',
      redirectPath
    );

    log('Current origin:', currentOrigin);
    log('Authority origin:', authorityOrigin);
    log('Consumer origin:', targetConsumerOrigin);
    log('Consumer mode:', consumer);
    log('Authority host:', authorityHost);
    log('Authority /auth/google:', authorityGoogleUrl);
    log('Transfer begin URL:', transferBeginUrl);
    log('Should transfer to consumer:', shouldTransferToConsumer);

    let phase = 'start';
    let pollCount = 0;
    const maxPolls = 80;
    const pollDelay = 400;

    try {
      if (!consumer) {
        const pre = await fetchJsonWithCreds(authorityMeUrl, 'precheck authority (authority-page)');
        if (pre.ok) {
          if (shouldTransferToConsumer) {
            popup.location = transferBeginUrl;
            phase = 'waiting-consumer-session';
          } else {
            popup.location = authorityGoogleUrl;
            phase = 'authority-login-only';
          }
        } else {
          popup.location = authorityGoogleUrl;
          phase = shouldTransferToConsumer ? 'ensure-authority-login-then-transfer' : 'authority-login-only';
        }
      } else {
        const pre = await fetchJsonWithCreds(authorityMeUrl, 'precheck authority (consumer-page)');
        if (pre.ok) {
          popup.location = transferBeginUrl;
          phase = 'waiting-consumer-session';
        } else {
          popup.location = authorityGoogleUrl;
          phase = 'waiting-consumer-session';
        }
      }
    } catch (e) {
      warn('Failed to start popup navigation:', e);
      phase = 'start';
    }

    try {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    } catch {}
    pollTimerRef.current = null;

    pollTimerRef.current = setInterval(async () => {
      try {
        if (!popupRef.current || popupRef.current.closed) {
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          setIsLoggingIn(false);
          return;
        }

        pollCount += 1;

        if (!consumer) {
          if (phase === 'authority-login-only') {
            const r = await fetchJsonWithCreds(currentMeUrl, `poll authority-only current-origin #${pollCount}`);
            if (r.ok && r.body) {
              broadcastAuthChanged(currentOrigin, { type: 'oauth-success' });
              if (pollTimerRef.current) clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              setIsLoggingIn(false);
              if (setUser) setUser(r.body);
              try {
                popupRef.current.close();
              } catch {}
              popupRef.current = null;
              return;
            }
          } else if (phase === 'ensure-authority-login-then-transfer') {
            const ar = await fetchJsonWithCreds(authorityMeUrl, `poll ensure-authority-login (authority-page) #${pollCount}`);
            if (ar.ok) {
              try {
                popupRef.current.location = transferBeginUrl;
              } catch (e) {
                warn('Failed to set popup.location to transfer begin:', e);
              }
              phase = 'waiting-consumer-session';
            }
          } else if (phase === 'waiting-consumer-session') {
            const cr = await fetchJsonWithCreds(consumerMeUrl, `poll waiting-consumer-session (authority-page) #${pollCount}`);
            if (cr.ok && cr.body) {
              broadcastAuthChanged(currentOrigin, { type: 'oauth-success' });
              if (pollTimerRef.current) clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              setIsLoggingIn(false);
              if (setUser) setUser(cr.body);
              try {
                popupRef.current.close();
              } catch {}
              popupRef.current = null;
              return;
            }
          } else {
            const r = await fetchJsonWithCreds(currentMeUrl, `poll fallback current-origin #${pollCount}`);
            if (r.ok && r.body) {
              broadcastAuthChanged(currentOrigin, { type: 'oauth-success' });
              if (pollTimerRef.current) clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              setIsLoggingIn(false);
              if (setUser) setUser(r.body);
              try {
                popupRef.current.close();
              } catch {}
              popupRef.current = null;
              return;
            }
          }
        } else {
          if (phase === 'waiting-consumer-session') {
            const cr = await fetchJsonWithCreds(consumerMeUrl, `poll waiting-consumer-session (consumer-page) #${pollCount}`);
            if (cr.ok && cr.body) {
              broadcastAuthChanged(currentOrigin, { type: 'oauth-success' });
              if (pollTimerRef.current) clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              setIsLoggingIn(false);
              if (setUser) setUser(cr.body);
              try {
                popupRef.current.close();
              } catch {}
              popupRef.current = null;
              return;
            }
          } else {
            const cr = await fetchJsonWithCreds(consumerMeUrl, `poll fallback consumer current-origin #${pollCount}`);
            if (cr.ok && cr.body) {
              broadcastAuthChanged(currentOrigin, { type: 'oauth-success' });
              if (pollTimerRef.current) clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              setIsLoggingIn(false);
              if (setUser) setUser(cr.body);
              try {
                popupRef.current.close();
              } catch {}
              popupRef.current = null;
              return;
            }
          }
        }

        if (pollCount >= maxPolls) {
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setIsLoggingIn(false);
          setError('Login timed out. Please try again.');
          try {
            popupRef.current.close();
          } catch {}
          popupRef.current = null;
        }
      } catch (e) {
        warn('Error during login polling:', e);
        if (pollCount >= maxPolls) {
          if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setIsLoggingIn(false);
          setError('Login failed. Please try again.');
          try {
            popupRef.current.close();
          } catch {}
          popupRef.current = null;
        }
      }
    }, pollDelay);
  };

  const text = user
    ? user.username || user.name || user.email || 'Account'
    : label || (small ? 'Log in' : 'Log in with Google');

  const className = small ? 'login-button login-button--small' : 'login-button';

  return (
    <div className="login-button-wrapper">
      <button type="button" className={className} onClick={handleLoginClick} disabled={isLoggingIn}>
        {isLoggingIn ? 'Logging in…' : text}
      </button>

      {hasPopupBlocked && <div className="login-warning">Please allow popups for this site to log in.</div>}

      {error && <div className="login-error">{error}</div>}

      {isLoggingIn ? (
        <button
          type="button"
          className={small ? 'login-button login-button--small' : 'login-button'}
          onClick={stopLoginFlow}
          style={{ marginTop: 8 }}
        >
          Cancel
        </button>
      ) : null}
    </div>
  );
}

export default LoginButton;